import { useState, useCallback } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { formatPrice, formatPercent, formatNumber } from '../../lib/formatters';
import { getAllInCostPerUnit, getNetIncomePerUnit } from '../../lib/calculations';
import type { PackageConfig, PnLResult, Assumptions } from '../../types';
import type { BuyerProxyResult } from '../../lib/buyerProxy';
import type { StrategicDirection } from '../../lib/strategicDirections';

interface ExportProps {
 config?: PackageConfig;
 pnl?: PnLResult;
 buyerProxy?: BuyerProxyResult;
 direction?: StrategicDirection;
 generatedContent?: string | null;
 assumptions?: Assumptions;
}

/* ---------- Markdown → HTML with semantic structure ---------- */

function markdownToHtml(md: string): string {
 let html = md
  // Headers
  .replace(/^### (.+)$/gm, (_m, title) =>
   `<h3 class="subsection">${title}</h3>`)
  .replace(/^## (.+)$/gm, (_m, title) =>
   `<h2 class="section-heading">${title}</h2>`)
  // Bold tagline-style quotes
  .replace(/\*\*"(.+?)"\*\*/g,
   '<span class="tagline-highlight">"$1"</span>')
  // Bold + italic
  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  .replace(/\*(.+?)\*/g, '<em>$1</em>')
  // Blockquotes
  .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
  // Ordered list items
  .replace(/^\d+\.\s+(.+)$/gm, '<li class="ol-item">$1</li>')
  // Unordered list items
  .replace(/^- (.+)$/gm, '<li class="ul-item">$1</li>');

 // Wrap consecutive <li> blocks in <ul>
 html = html.replace(/(<li class="[uo]l-item">.*?<\/li>\n?)+/gs, (match) => {
  const isOrdered = match.includes('class="ol-item"');
  const tag = isOrdered ? 'ol' : 'ul';
  return `<${tag} class="content-list">${match}</${tag}>`;
 });

 // Paragraphs: double newlines become <p>, single newlines become <br/>
 html = html
  .replace(/\n\n/g, '</p><p>')
  .replace(/\n/g, '<br/>');

 // Wrap in paragraph tags if not already structured
 if (!html.startsWith('<')) {
  html = `<p>${html}</p>`;
 }

 return html;
}

/* ---------- Section accent colors for PDF ---------- */

function sectionAccent(title: string): string {
 const t = title.toLowerCase();
 if (t.includes('tagline')) return '#8B5CF6';
 if (t.includes('pitch') || t.includes('elevator')) return '#3B82F6';
 if (t.includes('landing') || t.includes('headline') || t.includes('value prop')) return '#14B8A6';
 if (t.includes('email')) return '#F59E0B';
 if (t.includes('social')) return '#EC4899';
 if (t.includes('campaign') || t.includes('brief')) return '#7C3AED';
 return '#14B8A6';
}

/* ---------- Build full export HTML document ---------- */

function buildExportHTML(config: PackageConfig, pnl: PnLResult, proxy: BuyerProxyResult, direction: StrategicDirection, content: string, assumptions: Assumptions): string {
 const d = proxy.demographics;
 const b = proxy.conjointBehavior;
 const allInCost = getAllInCostPerUnit(config.enabledFeatures, assumptions);
 const netIncome = Math.round(getNetIncomePerUnit(config.price, config.enabledFeatures, assumptions));

 // Parse sections from markdown to apply per-section accent colors
 const sectionBlocks: { title: string; content: string; accent: string }[] = [];
 const lines = content.split('\n');
 let currentTitle = '';
 let currentBuf: string[] = [];
 for (const line of lines) {
  const m = line.match(/^## (.+)/);
  if (m) {
   if (currentTitle) sectionBlocks.push({ title: currentTitle, content: currentBuf.join('\n').trim(), accent: sectionAccent(currentTitle) });
   currentTitle = m[1].trim();
   currentBuf = [];
  } else {
   currentBuf.push(line);
  }
 }
 if (currentTitle) sectionBlocks.push({ title: currentTitle, content: currentBuf.join('\n').trim(), accent: sectionAccent(currentTitle) });

 const materialsHtml = sectionBlocks.map((sec, i) => `
  <div class="material-section" style="--accent: ${sec.accent};">
   <div class="material-header">
    <div class="material-number" style="background: ${sec.accent};">${i + 1}</div>
    <h2 class="material-title">${sec.title}</h2>
   </div>
   <div class="material-body">
    ${markdownToHtml(sec.content)}
   </div>
  </div>
 `).join('\n');

 const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

 return `<!DOCTYPE html>
<html>
<head>
 <meta charset="utf-8">
 <title>AlterMe Marketing Package — ${config.name} — ${direction.name}</title>
 <link rel="preconnect" href="https://fonts.googleapis.com">
 <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
 <style>
  @page { margin: 0.6in 0.75in; size: letter; }
  * { box-sizing: border-box; margin: 0; padding: 0; }

  body {
   font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
   color: #1e293b;
   line-height: 1.6;
   background: #fff;
   -webkit-print-color-adjust: exact;
   print-color-adjust: exact;
  }

  .page { max-width: 780px; margin: 0 auto; padding: 40px 32px; }

  /* ---- Cover ---- */
  .cover {
   text-align: center;
   padding: 64px 40px 48px;
   margin-bottom: 40px;
   border-bottom: 3px solid #14B8A6;
   position: relative;
  }
  .cover::before {
   content: '';
   position: absolute;
   top: 0; left: 0; right: 0;
   height: 4px;
   background: linear-gradient(90deg, #14B8A6, #0891B2, #8B5CF6);
  }
  .cover .brand { font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #14B8A6; margin-bottom: 12px; }
  .cover h1 { font-size: 30px; font-weight: 800; color: #0f172a; margin-bottom: 6px; letter-spacing: -0.02em; }
  .cover .pkg { font-size: 17px; color: #475569; font-weight: 500; margin-bottom: 4px; }
  .cover .direction-label { font-size: 14px; color: #14B8A6; font-weight: 600; margin-bottom: 16px; }
  .cover .meta { font-size: 11px; color: #94a3b8; }

  /* ---- Direction Banner ---- */
  .direction-banner {
   background: linear-gradient(135deg, #f0fdfa 0%, #f8fafc 100%);
   border: 1px solid #99f6e4;
   border-radius: 12px;
   padding: 24px 28px;
   margin-bottom: 32px;
  }
  .direction-banner .label {
   font-size: 10px; font-weight: 700; text-transform: uppercase;
   letter-spacing: 0.1em; color: #14B8A6; margin-bottom: 6px;
  }
  .direction-banner h2 { font-size: 19px; font-weight: 700; color: #0f172a; margin-bottom: 8px; }
  .direction-banner .positioning { font-size: 13px; color: #334155; line-height: 1.65; margin-bottom: 14px; }
  .direction-banner .meta-grid {
   display: flex; gap: 20px; flex-wrap: wrap;
   padding-top: 12px; border-top: 1px solid #d1fae5;
  }
  .direction-banner .meta-item { font-size: 11px; color: #64748b; }
  .direction-banner .meta-item strong { color: #0f172a; font-weight: 600; }

  /* ---- Data Tables ---- */
  .data-section { margin-bottom: 28px; page-break-inside: avoid; }
  .data-section-title {
   font-size: 11px; font-weight: 700; text-transform: uppercase;
   letter-spacing: 0.08em; color: #14B8A6; margin-bottom: 10px;
   padding-bottom: 6px; border-bottom: 2px solid #14B8A6;
  }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  tr:nth-child(even) { background: #f8fafc; }
  th {
   background: #0f172a; color: #e2e8f0; padding: 8px 14px;
   text-align: left; font-size: 10px; font-weight: 600;
   text-transform: uppercase; letter-spacing: 0.06em;
  }
  td { padding: 9px 14px; border-bottom: 1px solid #e2e8f0; }
  td.label { font-weight: 600; width: 42%; color: #334155; }
  td.value { color: #1e293b; }

  /* ---- Material Sections ---- */
  .materials-header {
   font-size: 11px; font-weight: 700; text-transform: uppercase;
   letter-spacing: 0.08em; color: #14B8A6; margin: 40px 0 20px;
   padding-bottom: 8px; border-bottom: 2px solid #14B8A6;
  }

  .material-section {
   margin-bottom: 28px;
   page-break-inside: avoid;
   border: 1px solid #e2e8f0;
   border-radius: 10px;
   overflow: hidden;
  }

  .material-header {
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 14px 20px;
   background: #f8fafc;
   border-bottom: 1px solid #e2e8f0;
  }

  .material-number {
   width: 26px; height: 26px;
   border-radius: 7px;
   color: #fff;
   font-size: 12px;
   font-weight: 700;
   display: flex;
   align-items: center;
   justify-content: center;
   flex-shrink: 0;
  }

  .material-title {
   font-size: 14px;
   font-weight: 700;
   color: #0f172a;
   margin: 0;
  }

  .material-body {
   padding: 18px 22px;
  }

  .material-body p {
   font-size: 12.5px;
   line-height: 1.7;
   color: #334155;
   margin: 6px 0;
  }

  .material-body strong { color: #0f172a; font-weight: 600; }
  .material-body em { color: #64748b; }

  .material-body .subsection {
   font-size: 11px;
   font-weight: 700;
   text-transform: uppercase;
   letter-spacing: 0.05em;
   color: var(--accent, #14B8A6);
   margin: 20px 0 8px;
   padding: 7px 10px;
   border-left: 3px solid var(--accent, #14B8A6);
   background: color-mix(in srgb, var(--accent, #14B8A6) 6%, #fff);
   border-radius: 0 6px 6px 0;
  }

  .material-body .subsection:first-child { margin-top: 0; }

  .material-body .section-heading {
   font-size: 14px;
   font-weight: 700;
   color: #0f172a;
   margin: 24px 0 10px;
   padding-bottom: 6px;
   border-bottom: 2px solid var(--accent, #14B8A6);
  }

  .material-body .tagline-highlight {
   display: block;
   font-size: 13px;
   font-weight: 600;
   color: #0f172a;
   padding: 10px 14px;
   background: color-mix(in srgb, var(--accent, #14B8A6) 6%, #fff);
   border-left: 3px solid var(--accent, #14B8A6);
   border-radius: 0 8px 8px 0;
   margin: 6px 0;
  }

  .material-body blockquote {
   border-left: 3px solid color-mix(in srgb, var(--accent, #14B8A6) 40%, #fff);
   padding: 10px 14px;
   margin: 10px 0;
   font-style: italic;
   color: #64748b;
   background: color-mix(in srgb, var(--accent, #14B8A6) 4%, #fff);
   border-radius: 0 8px 8px 0;
   font-size: 12px;
   line-height: 1.65;
  }

  .material-body .content-list {
   padding-left: 18px;
   margin: 8px 0;
  }

  .material-body .content-list li {
   font-size: 12.5px;
   line-height: 1.65;
   color: #334155;
   margin: 4px 0;
   padding-left: 4px;
  }

  .material-body ol.content-list {
   counter-reset: pdf-ol;
   padding-left: 0;
   list-style: none;
  }

  .material-body ol.content-list li {
   counter-increment: pdf-ol;
   padding-left: 28px;
   position: relative;
  }

  .material-body ol.content-list li::before {
   content: counter(pdf-ol);
   position: absolute;
   left: 0; top: 4px;
   width: 20px; height: 20px;
   border-radius: 5px;
   background: color-mix(in srgb, var(--accent, #14B8A6) 10%, #fff);
   color: var(--accent, #14B8A6);
   font-size: 10px;
   font-weight: 700;
   line-height: 20px;
   text-align: center;
  }

  /* ---- Footer ---- */
  .footer {
   margin-top: 48px;
   padding-top: 16px;
   border-top: 2px solid #e2e8f0;
   text-align: center;
  }
  .footer p { font-size: 10px; color: #94a3b8; margin: 2px 0; }
  .footer .brand-mark { font-size: 9px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #14B8A6; margin-bottom: 4px; }

  @media print {
   .page { padding: 0; }
   .material-section { break-inside: avoid; }
   .data-section { break-inside: avoid; }
  }
 </style>
</head>
<body>
 <div class="page">

  <div class="cover">
   <div class="brand">GHOST AI &middot; AlterMe</div>
   <h1>Marketing Package</h1>
   <div class="pkg">${config.name} &mdash; ${formatPrice(config.price)}</div>
   <div class="direction-label">Direction: ${direction.name}</div>
   <div class="meta">Generated ${dateStr} &middot; Source: CBC Conjoint Study (n=1,001)</div>
  </div>

  <div class="direction-banner">
   <div class="label">Strategic Direction</div>
   <h2>${direction.name}</h2>
   <div class="positioning">${direction.positioningStatement}</div>
   <div class="meta-grid">
    <div class="meta-item"><strong>Tone:</strong> ${direction.tone}</div>
    <div class="meta-item"><strong>Lead Pillar:</strong> ${direction.leadPillar}</div>
    <div class="meta-item"><strong>Risk Level:</strong> ${direction.riskLevel}</div>
    ${direction.targetAudience ? `<div class="meta-item"><strong>Target:</strong> ${direction.targetAudience}</div>` : ''}
   </div>
  </div>

  <div class="data-section">
   <div class="data-section-title">1. Package Overview</div>
   <table>
    <tr><th colspan="2">Package Economics</th></tr>
    <tr><td class="label">Price</td><td class="value">${formatPrice(config.price)}</td></tr>
    <tr><td class="label">Effective Price (after ${formatPercent(assumptions.totalDeductionRate, 0)} deductions)</td><td class="value">${formatPrice(Math.round(config.price * (1 - assumptions.totalDeductionRate)))}</td></tr>
    <tr><td class="label">Features (${config.enabledFeatures.length} of 7)</td><td class="value">${config.enabledFeatures.join(', ')}</td></tr>
    <tr><td class="label">All-In Cost / Unit</td><td class="value">${formatPrice(Math.round(allInCost))}</td></tr>
    <tr><td class="label">Net Income / Unit (CFO)</td><td class="value">${formatPrice(netIncome)}</td></tr>
   </table>
  </div>

  <div class="data-section">
   <div class="data-section-title">2. Market Opportunity</div>
   <table>
    <tr><th colspan="2">Demand &amp; Revenue</th></tr>
    <tr><td class="label">Total Addressable (scenario)</td><td class="value">${formatNumber(pnl.s2Units)} S2 units/month</td></tr>
    <tr><td class="label">Conjoint Demand Rate</td><td class="value">${formatPercent(pnl.adjustedChoiceRate)}</td></tr>
    <tr><td class="label">Gross Revenue</td><td class="value">${formatPrice(pnl.grossRevenue)}/month</td></tr>
    <tr><td class="label">Net Revenue</td><td class="value">${formatPrice(pnl.netRevenue)}/month</td></tr>
    <tr><td class="label">Scenario P&amp;L</td><td class="value">${formatPrice(pnl.monthlyPnL)}/month</td></tr>
    <tr><td class="label">Required Close Rate</td><td class="value">${formatPercent(pnl.impliedCloseRate)} (vs. 30% proven)</td></tr>
   </table>
  </div>

  <div class="data-section">
   <div class="data-section-title">3. Audience Persona (${proxy.count} respondent proxy)</div>
   <table>
    <tr><th colspan="2">Demographics &amp; Behavior</th></tr>
    <tr><td class="label">Demographics</td><td class="value">Age ${d.ageRange[0]}&ndash;${d.ageRange[1]} (median ${d.medianAge}) &middot; ${d.femalePercent}% Female &middot; ${d.malePercent}% Male</td></tr>
    <tr><td class="label">Top Income</td><td class="value">${d.topIncomes[0]?.label ?? 'N/A'} (${d.topIncomes[0]?.percent ?? 0}%)</td></tr>
    <tr><td class="label">Regions</td><td class="value">${d.topRegions.map(r => `${r.label} (${r.percent}%)`).join(', ')}</td></tr>
    <tr><td class="label">Brand Awareness</td><td class="value">${d.brandAwareness.neverHeardPercent}% never heard of AlterMe</td></tr>
    <tr><td class="label">Top WTP Factors</td><td class="value">${d.topWtpFactors.slice(0, 3).map(f => `${f.label} (${f.percentage}%)`).join(', ')}</td></tr>
    <tr><td class="label">Top Health Goals</td><td class="value">${d.topGoals.slice(0, 3).map(g => `${g.label} (${g.percentage}%)`).join(', ')}</td></tr>
    <tr><td class="label">Conjoint Behavior</td><td class="value">Avg ${(15 - b.avgNoneRate).toFixed(1)}/15 products chosen${b.avgChosenPrice ? `, avg price ${formatPrice(b.avgChosenPrice)}` : ''}</td></tr>
   </table>
  </div>

  <div class="materials-header">4. Marketing Materials &mdash; &ldquo;${direction.name}&rdquo; Direction</div>
  ${materialsHtml}

  <div class="footer">
   <div class="brand-mark">AlterMe Conjoint Simulator</div>
   <p>All data sourced from CBC Conjoint Study (n=1,001, February 2026).</p>
   <p>Strategic direction and marketing materials are data-driven starting points &mdash; review and customize before deployment.</p>
  </div>

 </div>
</body>
</html>`;
}

export function MarketingPackageExport({ config, pnl, buyerProxy, direction, generatedContent, assumptions }: ExportProps) {
 const [generating, setGenerating] = useState(false);

 const isReady = !!(config && pnl && buyerProxy && direction && generatedContent && assumptions);

 const handleExport = useCallback(() => {
  if (!isReady) return;
  setGenerating(true);
  setTimeout(() => {
   const html = buildExportHTML(config!, pnl!, buyerProxy!, direction!, generatedContent!, assumptions!);
   const printWindow = window.open('', '_blank');
   if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); setGenerating(false); }, 500);
   } else {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AlterMe-Marketing-${config!.name}-${direction!.name.replace(/\s+/g, '-')}.html`;
    a.click();
    URL.revokeObjectURL(url);
    setGenerating(false);
   }
  }, 100);
 }, [isReady, config, pnl, buyerProxy, direction, generatedContent, assumptions]);

 return (
  <button
   onClick={handleExport}
   disabled={generating || !isReady}
   title={isReady ? 'Download as PDF' : 'Generate marketing materials first'}
   className={`flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg transition-colors shadow-sm ${
    isReady
     ? 'bg-chart-teal text-white hover:bg-teal-600'
     : 'bg-gray-100 text-gray-400 cursor-not-allowed'
   }`}
  >
   {generating ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
   {generating ? 'Generating...' : 'Download Package'}
  </button>
 );
}
